import angular from "angular";

import { EditDialogKnownActions, RowTypeService, UiHelpers } from "../../services";
import { CalcRowConfig, CalcRowsLogic, CalculationRowCategory, CalculationRowSource, CalculationRowType, CalculatorLogic, Employment, InputEnumOption, UserDefinedRowLogic, RowTypeDefinition, RowsUsecaseLogic, UserDefinedRow, EnumerationsLogic, IncomeTypesLogic, CalcRowConfigTypeGroup } from "@salaxy/core";

/**
 * Controller for editing the row types in settings (Palkkalajit)
 */
export class RowTypesSettingsController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["RowTypeService", "UiHelpers", "$timeout", "$element"];

  /**
   * Set this property if you want to explicitly set the model (currently only Employment).
   * If undefinded, the component binds to company settings.
   */
  public model?: Employment;

  /**
   * Defines the type of rows editor: "builtIn" for built-in rows,
   * "custom" for custom rows, "prices" company specific default prices per row type,
   * "copy" for rows that should be copied to the calculation.
   */
  public mode: "builtIn" | "custom" | "prices" | "copy";

  /** If set, filters the rows based on categories (plus rowTypes if set) */
  public categories: CalculationRowCategory[];

  /** Search text that is potentially input by the user. */
  public searchText: string;

  private listCache: {
    /** Cache key */
    key: string,
    /** Cache value */
    value: CalcRowConfig[],
  } = null;

  constructor(private rowTypeService: RowTypeService, private uiHelpers: UiHelpers, private $timeout: angular.ITimeoutService, private $element: JQLite) {
  }

  /** New empty row for databinding when creating a new row. */
  public newRow: RowTypeDefinition;

  /** Sets the default values in init. */
  public $onInit() {
    this.newRow = this.getBlank();
  }

  /** Saves the changes to row types. */
  public save() {
    this.rowTypeService.save();
    this.listCache = null;
  }

  /**
   * Gets the current object: Either the rows collection from settings as soon as it is loaded or an employment relation.
   */
  public get current(): RowTypeDefinition[] | Employment | undefined {
    if (this.model) {
      return this.model;
    }
    return this.rowTypeService.isReady ? this.rowTypeService.allCustomRows : undefined;
  }

  /**
   * List of items to edit: In settings or Employment relation.
   */
  public get list(): RowTypeDefinition[] {
    if ((this.current as Employment)?.work?.salaryDefaults) {
      return (this.current as Employment).work.salaryDefaults as RowTypeDefinition[];
    }
    return this.rowTypeService.allCustomRows;
  }

  /**
   * Gets the price rows in the current modeel.
   */
  public get pricesList(): RowTypeDefinition[] {
    return this.list.filter(x => x.source === CalculationRowSource.PriceCompany || x.source == CalculationRowSource.PriceEmployment) as RowTypeDefinition[];
  }

  /**
   * Gets the copy rows in the current model.
   */
  public get copyRowsList(): RowTypeDefinition[] {
    return this.list.filter(x => x.source === CalculationRowSource.CopyCompany || x.source == CalculationRowSource.CopyEmployment) as RowTypeDefinition[];
  }

  /**
   * Gets the default rows from the current model
   */
  public get defaultsList(): RowTypeDefinition[] {
    return this.list.filter(x => !x.source || x.source === CalculationRowSource.Undefined || x.source == CalculationRowSource.ManualRow) as RowTypeDefinition[];
  }

  /**
   * Gets the list of company specific rows, always in settings even if the bound model is employment.
   */
  public get settingsList(): RowTypeDefinition[] {
    if (!this.rowTypeService.isReady) {
      console.error("Settings not yet loaded, handle this edge case.");
      return undefined;
    }
    return this.rowTypeService.allCustomRows;
  }

  /**
   * Gets the configuration object for a row definition.
   * @param row Row for which the configuration is fetched.
   * @returns The configuration for the row.
   */
  public getConfig(row: RowTypeDefinition): CalcRowConfig {
    return this.rowTypeService.getConfig(row);
  }

  /** Creating of a new item. */
    public getBlank(): RowTypeDefinition {
      const nextId = Math.max(...this.list.map(x => x.rowIndex), 0) + 1;
      const newRow = UserDefinedRowLogic.getBlank(nextId) as RowTypeDefinition;
      newRow.config = {} as any;
      return newRow;
    }

  /**
   * Gets the search as cached item: Can be bound to ng-repeat.
   * @param searchText The search text. If null or empty, returns the full list.
   */
  public getCachedSearch(searchText: string): InputEnumOption[] {
    return this.uiHelpers.cache(this, "search",
      () => {
        return this.search(searchText, (this.mode == "builtIn" || this.mode == "custom") ? this.mode : undefined);
      },
      () => this.listCache?.key + this.rowTypeService.timestamp + searchText + this.categories?.toString() + this.mode);
  }

  /**
   * Searches the row types.
   * @param searchText The search text. If null or empty, returns the full list.
   */
  public search(searchText: string, group: "builtIn" | "custom" | undefined): InputEnumOption[] {
    const toOption = (config: CalcRowConfig) => {
      return {
        text: config.label,
        value: config.name,
        title: config.descr,
        ui: {
          color: config.color,
          iconText: config.iconText,
          addedByChild: null,
          // TODO: Requires refactoring
          group: config.group,
          type: config.type,
          rowType: config.rowType,
        },
      };
    };
    const result: InputEnumOption[] = [];
    searchText = (searchText || "").trim().toLowerCase();
    this.getRowTypes(group).forEach((rowConfig) => {
      let isParentAdded = false;
      if (
        !searchText
        || (rowConfig.label + rowConfig.descr + rowConfig.name).toLowerCase().indexOf(searchText) >= 0
      ) {
        result.push(toOption(rowConfig));
        isParentAdded = true;
      }
      if (rowConfig.kind?.values) {
        rowConfig.kind.values.forEach((kind) => {
          if (
            !(kind.name == "undefined" || kind.name == "unknown") &&
            (searchText && (kind.label + kind.descr + kind.name).toLowerCase().indexOf(searchText) >= 0)
          ) {
            if (!isParentAdded) {
              const option = toOption(rowConfig);
              option.ui.addedByChild = true;
              result.push(option);
              isParentAdded = true;
            }
            result.push({
              text: kind.label,
              value: rowConfig.name + "." + kind.name,
              title: kind.descr,
              // TODO: Requires refactoring
              ui: {
                group: rowConfig.group,
                type: rowConfig.type,
                rowType: rowConfig.rowType,
              }
            });
          }
        });
      }
    });
    if (result.length === 2 && result[0].ui.addedByChild) {
      // If only one parent with one child (child is exact match) => Merge to one row.
      return [{
        text: result[0].text + ": " + result[1].text,
        value: result[1].value,
        title: result[1].title,
        ui: result[0].ui,
      }];
    }
    return result;
  }

  /**
   * Opens an edit dialog for price or default row => Opening just a normal dialog.
   * @param row Row to edit: This should be a price row or default row: Not a custom row.
   */
  public showEdit(row: RowTypeDefinition) {
    if (!row) {
      throw new Error("showEditDialog called without a parameter.");
    }
    const isNew = false; // Add if necessary
    const calc = CalculatorLogic.getBlank();
    calc.usecase.data.standAloneRows = true;
    calc.isReadOnly = false;

    this.uiHelpers.openEditDialog("salaxy-components/calc/rows/CalcRows2019EditDialog.html", row, {
      calc,
    }, "", "CalcRows2019Controller").then((result) => {
      if (result.action === "ok-no-save") {
        if (isNew) {
          this.list.push(result.item);
        }
      } else if (result.action === "delete-no-save") {
        if (!isNew) {
          this.delete(row);
        }
      } else {
        // No changes, cancel
      }
    });
  }

  /**
   * Deletes a row. This does not save changes.
   * @param row Row to delete.
   */
  public delete(row: RowTypeDefinition) {
    this.list.splice(this.list.indexOf(row), 1);
  }

  /** Copies the given text to clipboard. */
  public copyToClipboard(text: string) {
    navigator.clipboard.writeText(text);
  }

  /**
   * Commits the new row in newRow property and adds it to the rows list.
   * Updates usecase and sets the newRow as new blank row.
   */
  public commitNewRow(source: CalculationRowSource | "price" | "copy", showEdit = false): RowTypeDefinition {
    if (!this.newRow?.type) {
      return null;
    }
    const row = this.getBlank();
    if (source == "price") {
      row.source = this.model?.work ? CalculationRowSource.PriceEmployment : CalculationRowSource.PriceCompany;
    } else if (source == "copy") {
      row.source = this.model?.work ? CalculationRowSource.CopyEmployment : CalculationRowSource.CopyCompany;
    } else {
      row.source = source;
    }
    if (this.newRow.config?.group === "custom") {
      // Custom row type
      row.type = this.newRow.type;
      // HACK: Not sure if we should store the config here => Should probably fetch it on-the-fly when presenting the row.
      row.config = this.getRowTypes(undefined).find(x => x.type === row.type);
      if (!row.config) {
        throw new Error("Row type not found in config: " + row.type);
      }
      row.rowType = row.config.rowType;
      const settingsRow = this.settingsList.find(x => x.type === row.type);
      if (!settingsRow) {
        throw new Error("Row type not found in settings: " + row.type);
      }
      row.data.kind = settingsRow.data.kind;
      row.price = settingsRow.price;
      this.rowTypeService.applyDefaultsToNewRow(row, undefined);
    } else {
      // Old logic
      row.rowType = this.newRow.rowType;
      row.data.kind = this.newRow.data.kind;
      row.type = this.newRow.type;
      row.config = {
        group: "builtIn",
      } as any;
    }

    RowsUsecaseLogic.getUsecaseLogicForCalc(row, CalculatorLogic.getBlank(), true);
    this.list.push(row);
    // TODO: Consider adding this => Does it make sense in prices / default rows?
    // this.rowTypeChanged(row);
    this.$timeout(() => {
      // Reset new row type after timeout to avoid debounce
      this.newRow = this.getBlank();
    }, 300);
    if (showEdit) {
      this.showEdit(row); // TODO: What was the relevance of this: , "auto");
    } else {
      this.$timeout(() => {
        let elem: HTMLElement = null;
        elem = (this.$element[0] as HTMLElement).querySelector(
          "#iptPrice" + row.rowIndex
        );
        if (elem) {
          elem.focus();
        }
      }, 500);
    }
    return row;
  }

  /**
   * Shows a dialog for editing the row-type or that shows information about the row-type if read-only.
   * @param rowType The row type to show information about.
   */
  public showRowTypeDetails(rowType: InputEnumOption) {
    let editableType: RowTypeDefinition = this.settingsList
      .find(x => x.source == CalculationRowSource.CustomRowtype && (x as RowTypeDefinition).config.name == rowType.value) as RowTypeDefinition;
    if (!editableType) {
      // Not (an existing) custom row type => Show read-only dialog for a built-in type.
      editableType = {
        rowIndex: 0,
        rowType: rowType.value as CalculationRowType,
        source: CalculationRowSource.CustomRowtype,
        data: {
          dimensions: {},
          accounting: {},
        },
        config: structuredClone(CalcRowsLogic.getRowConfig(rowType.value as CalculationRowType)),
      };
      editableType.config.group =  CalcRowConfigTypeGroup.BuiltIn;
    }
    if (!editableType) {
      throw new Error("Row type not found: " + rowType.value);
    }
    this.uiHelpers.openEditDialog("salaxy-components/calc/rows/RowTypeEditDialog.html", editableType, {
      calc: CalculatorLogic.getBlank(),
      definitionEditor: this.getDialogLogic(editableType.config.group),
    }, "lg", "CalcRows2019Controller",).then((_result) => {
      if (_result.action == EditDialogKnownActions.Ok) {
        this.rowTypeService.save();
        this.listCache = null;
      }
    });
  }

  private getDialogLogic(mode: "builtIn" | "custom") {
    return {
      // Perhaps this is not really necessary => You could just get it from the config?
      mode,
    };
  }

  /**
   * Creates a new custom row type.
   * @param rowType Base row type for the custom type.
   */
  public async createCustomRowType(rowType: InputEnumOption): Promise<void> {
    const name = prompt("Syötä rivityypille ID (a-z, 0-9, _):");
    if (!name) {
      return;
    }
    if (name.match(/[^a-zA-Z0-9_]/)) {
      alert("Virheellinen ID");
      return;
    }
    if (this.getRowTypes(undefined).find((x) => x.name === name)) {
      alert(`ID on '${name}' jo käytössä`);
      return;
    }
    const config = structuredClone(CalcRowsLogic.getRowConfig(rowType.value as CalculationRowType));
    config.group = CalcRowConfigTypeGroup.Custom;
    config.name = name;
    config.type = "/custom/" + name;
    config.label = name + " (uusi)";

    const editableType: RowTypeDefinition = {
      type: "/custom/" + name,
      rowType: rowType.value as CalculationRowType,
      rowIndex: 0,
      // HACK: Custom only if really custom
      source: CalculationRowSource.CustomRowtype,
      data: {
        dimensions: {},
        accounting: {},
        scripting: {}
      },
      config,
    };
    const _result = await this.uiHelpers.openEditDialog("salaxy-components/calc/rows/RowTypeEditDialog.html", editableType, {
      calc: CalculatorLogic.getBlank(),
      definitionEditor: this.getDialogLogic("custom"),
    }, "lg", "CalcRows2019Controller");
    if (_result.action == EditDialogKnownActions.Ok) {
      this.rowTypeService.allCustomRows.push(editableType);
      this.rowTypeService.save().then(() => {
        // TODO: Best UI would be if the tab would be changed from built-in to custom here.
      });
      this.listCache = null;
    }
  }

  /**
   * Deletes a custom row type.
   * @param rowType The row type to delete.
   */
  public deleteCustomRowType(rowType: InputEnumOption) {
    if (!confirm("Haluatko poistaa rivityypin '" + rowType.text + "'?")) {
      return;
    }
    this.rowTypeService.deleteRow(this.settingsList.find(x => x.source == CalculationRowSource.CustomRowtype && (x as RowTypeDefinition).config.name == rowType.value));
    this.rowTypeService.save();
  }

  /**
   * Returns placeholder text for row
   * NOTE: Copied from CalcRows2019Controller - This will be refactored into a logic class!
   * @param row used row
   * @returns row message as string
   */
  public getMessagePlaceholderText(row: UserDefinedRow): string {
    if (row.message) {
      return row.message;
    }
    let label = "";
    if (row.rowType === CalculationRowType.IrIncomeType) {
      label = IncomeTypesLogic.getAll().find(
        (x) =>
          (row.data?.kind && x.transactionCode === row.data.kind) ||
          (row.data?.irData?.code > 0 && x.code === row.data.irData.code)
      )?.label;
    } else if (row.rowType == CalculationRowType.AbsencePeriod) {
      label = EnumerationsLogic.getEnumLabel(
        "AbsenceCauseCode",
        row.data?.kind
      );
    } else if (
      [
        CalculationRowType.DailyAllowance,
        CalculationRowType.EmploymentTermination,
        CalculationRowType.WorkingTimeCompensation,
        CalculationRowType.Remuneration,
        CalculationRowType.OtherCompensation,
        CalculationRowType.NonProfitOrg,
        CalculationRowType.MealBenefit,
        CalculationRowType.Training,
        CalculationRowType.PaidSickLeave,
        CalculationRowType.TotalWorkerPayment,
        CalculationRowType.TimeRatePay,
      ].includes(row.rowType) &&
      row.data?.kind
    ) {
      label = EnumerationsLogic.getEnumLabel(
        `${row.rowType}Kind`,
        row.data.kind
      );
    }

    return (
      label || EnumerationsLogic.getEnumLabel("CalculationRowType", row.rowType)
    );
  }

  /**
   * Gets the list of row types. Method filters based on rowTypes and categories,
   * the texts are language versioned.
   */
  private getRowTypes(group: "builtIn" | "custom" | undefined): CalcRowConfig[] {
    if (!this.rowTypeService.isReady) {
      console.error("Row types not yet loaded, handle this edge case.");
      return [];
    }
    const result = this.rowTypeService.getConfigs(group, this.categories);
    return result;
  }

}
